import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import create from 'zustand';
import { persist } from 'zustand/middleware';

import { OrderLine } from '../orders/types';
import { ReturnInfos, ReturnLine } from '../returns/types';

// Register this module for debug tracking
debug.registerComponent('returnStore');

export type ReturnState = {
  returnOrderLines: Record<string, Record<string, ReturnLine & OrderLine & ReturnInfos>>;
  isSubmittingReturn: boolean;
  beginReturnSubmission: () => boolean;
  endReturnSubmission: () => void;
  addReturn: (returnRecord: ReturnLine & OrderLine & ReturnInfos) => void;
  deleteReturn: (recordId: string) => void;
  deleteReturnLine: (recordId: {
    clientId: string;
    orderId: string;
    nart: string;
  }) => void;
  reset: () => void;
};

export const useReturnStore = create<ReturnState>(
  persist(
    (set, get) => ({
      returnOrderLines: {},
      isSubmittingReturn: false,
      beginReturnSubmission: () => {
        if (get().isSubmittingReturn) {
          debug.log('returnStore', 'Return submission already in progress');
          return false;
        }
        set({ isSubmittingReturn: true });
        return true;
      },
      endReturnSubmission: () => {
        set({ isSubmittingReturn: false });
      },
      addReturn: (returnRecord: ReturnLine & OrderLine & ReturnInfos) =>
        set((state) => {
          const mainKey = `${returnRecord.clientId}/${returnRecord.orderId}`;
          const subKey = returnRecord.nart;
          debug.log('returnStore', `Adding return: ${mainKey}/${subKey}`);

          const existingMainEntry = state.returnOrderLines[mainKey] || {};

          const updatedSubEntry = {
            ...existingMainEntry[subKey],
            ...returnRecord,
          };

          return {
            ...state,
            returnOrderLines: {
              ...state.returnOrderLines,
              [mainKey]: {
                ...existingMainEntry,
                [subKey]: updatedSubEntry,
              },
            },
          };
        }),
      deleteReturn: (recordId: string) =>
        set((state) => {
          debug.log('returnStore', `Deleting return: ${recordId}`);
          const newState = { ...state.returnOrderLines };
          delete newState[recordId];
          return { returnOrderLines: newState };
        }),
      deleteReturnLine: (recordId: { clientId: string; orderId: string; nart: string }) =>
        set((state) => {
          const mainKey = `${recordId.clientId}/${recordId.orderId}`;
          const subKey = recordId.nart;
          debug.log('returnStore', `Deleting return line: ${mainKey}/${subKey}`);
          const newState = { ...state.returnOrderLines };
          delete newState[mainKey][subKey];
          if (Object.keys(newState[mainKey]).length === 0) {
            delete newState[mainKey];
          }
          return { returnOrderLines: newState };
        }),
      reset: () =>
        set(() => {
          debug.log('returnStore', `Resetting store`);
          return { returnOrderLines: {} };
        }),
    }),
    {
      name: 'returnStore',
      getStorage: () => localStorage,
      partialize: (state) => ({ returnOrderLines: state.returnOrderLines }),
      version: 1,
      migrate: (persistedState: any, version: number) => {
        debug.log('returnStore', `Migrating store from version ${version} to 1`);
        // On version mismatch, reset to default state
        if (version !== 1) {
          return { returnOrderLines: {} };
        }
        return persistedState;
      },
    },
  ),
);

export const resetReturns = useReturnStore.getState().reset;

// Mark this module as initialized
debug.markInitialized('returnStore');
